import React from "react";
import { Link } from "@tanstack/react-router";

// Localized Figma assets (downloaded from the provided file)
import gridSvg from "../../../assets/Icon/Grid.svg";
import clipboardSvg from "../../../assets/Icon/Clipboard.svg";
import shieldSvg from "../../../assets/Icon/Shield.svg";
import boxSvg from "../../../assets/Icon/Box.svg";
import hardDriveSvg from "../../../assets/Icon/HardDrive.svg";
import fileTextSvg from "../../../assets/Icon/FileText.svg";
import editSvg from "../../../assets/Icon/Edit.svg";
import chevronRightSvg from "../../../assets/Chevron/ChevronRight.svg";

export type MenuItemData = {
  label: string;
  iconSrc: string;
  onClick?: () => void;
  href?: string;
};

export type MenuProps = {
  title?: string;
  items?: MenuItemData[];
  className?: string;
  splitIndex?: number; // default 5; optional divider position
  currentPath?: string;
  onActiveItemClick?: () => void;
};

function isExternalMenuHref(href: string) {
  return (
    href.startsWith("http://") ||
    href.startsWith("https://") ||
    href.endsWith(".pdf")
  );
}

function MenuItem({
  data,
  currentPath,
  onActiveItemClick,
}: {
  data: MenuItemData;
  currentPath?: string;
  onActiveItemClick?: () => void;
}) {
  const content = (
    <div className="w-full flex items-start gap-3 px-4 py-3 rounded-md">
      <img src={data.iconSrc} alt="" className="w-5 h-5" />
      <div className="flex-1 flex items-center justify-between">
        <p className="text-sm text-textDefault">{data.label}</p>
        <img src={chevronRightSvg} alt="" className="w-4 h-4" />
      </div>
    </div>
  );

  if (data.href) {
    if (isExternalMenuHref(data.href)) {
      return (
        <a
          href={data.href}
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full"
        >
          {content}
        </a>
      );
    }

    const isActive = !!onActiveItemClick && data.href === currentPath;
    if (isActive) {
      return (
        <div
          className="block w-full cursor-pointer"
          onClick={onActiveItemClick}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onActiveItemClick?.();
            }
          }}
          role="button"
          tabIndex={0}
        >
          {content}
        </div>
      );
    }

    return (
      <Link to={data.href} className="block w-full">
        {content}
      </Link>
    );
  }

  return (
    <button type="button" onClick={data.onClick} className="w-full">
      {content}
    </button>
  );
}

export function Menu({
  title = "タイトル",
  items,
  className,
  splitIndex = 5,
  currentPath,
  onActiveItemClick,
}: MenuProps) {
  const defaultItems: MenuItemData[] = [
    { label: "メニュー", iconSrc: gridSvg },
    { label: "メニュー", iconSrc: clipboardSvg },
    { label: "メニュー", iconSrc: shieldSvg },
    { label: "メニュー", iconSrc: boxSvg },
    { label: "メニュー", iconSrc: hardDriveSvg },
    { label: "メニュー", iconSrc: fileTextSvg },
    { label: "メニュー", iconSrc: editSvg },
  ];
  const defaultIcons = [
    gridSvg,
    clipboardSvg,
    shieldSvg,
    boxSvg,
    hardDriveSvg,
    fileTextSvg,
    editSvg,
  ];
  const list = items
    ? items.map((it, i) => ({
        ...it,
        iconSrc: it.iconSrc || defaultIcons[i] || gridSvg,
      }))
    : defaultItems;

  return (
    <div
      className={
        "bg-white w-[240px] max-w-[300px] min-w-[200px] rounded-lg border border-menuBorder shadow" +
        (className ? ` ${className}` : "")
      }
      data-name="Menu"
      data-node-id="1289:4377"
    >
      <div className="px-4 pt-2 pb-1">
        <p className="text-base font-semibold text-textDefault">{title}</p>
      </div>
      <div className="px-4 py-2">
        <div className="h-px bg-menuBorder" />
      </div>

      <div className="w-full">
        {list.slice(0, splitIndex).map((item, i) => (
          <MenuItem
            key={`item-top-${i}`}
            data={item}
            currentPath={currentPath}
            onActiveItemClick={onActiveItemClick}
          />
        ))}
        <div className="px-4 py-2">
          <div className="h-px bg-menuBorder" />
        </div>
        {list.slice(splitIndex).map((item, i) => (
          <MenuItem
            key={`item-bottom-${i}`}
            data={item}
            currentPath={currentPath}
            onActiveItemClick={onActiveItemClick}
          />
        ))}
      </div>
    </div>
  );
}

export default Menu;
